import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import MetricCard from '../shared/MetricCard';
import {
  SLDSLineChart,
  SLDSBarChart,
  SLDSDoughnutChart,
  SLDSRadarChart,
  SparkLine,
  SLDS_CHART_COLORS,
  SLDS_CHART_COLORS_ALPHA,
} from '../shared/ChartComponents';
import {
  generateRevenueData,
  generatePipelineByStage,
  generateTeamPerformance,
} from '../shared/SampleData';
import Button from '@salesforce/design-system-react/components/button';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Icon from '@salesforce/design-system-react/components/icon';
import ScopedNotification from '@salesforce/design-system-react/components/scoped-notification';
import Combobox from '@salesforce/design-system-react/components/combobox';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  textMuted: '#939393',
  white: '#ffffff',
  purple: '#9050e9',
  teal: '#06a59a',
};

const cardStyle = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  padding: '20px',
};

const sectionHeaderStyle = {
  fontSize: '16px',
  fontWeight: '700',
  color: COLORS.textPrimary,
  margin: '0 0 4px 0',
};

const sectionSubtitleStyle = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  margin: '0 0 16px 0',
};

const gridRow = (columns, gap = '16px') => ({
  display: 'grid',
  gridTemplateColumns: columns,
  gap,
});

// ---------------------------------------------------------------------------
// Timeframe multiplier helpers
// ---------------------------------------------------------------------------
const TIMEFRAME_FACTORS = {
  MTD: 1,
  QTD: 3.1,
  YTD: 11.8,
};

const TIMEFRAME_LABELS = {
  MTD: 'Month-to-Date',
  QTD: 'Quarter-to-Date',
  YTD: 'Year-to-Date',
};

// ---------------------------------------------------------------------------
// Department filter options
// ---------------------------------------------------------------------------
const DEPARTMENT_OPTIONS = [
  { id: 'all', label: 'All Departments' },
  { id: 'sales', label: 'Sales' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'customer-success', label: 'Customer Success' },
  { id: 'finance', label: 'Finance' },
  { id: 'operations', label: 'Operations' },
];

// ---------------------------------------------------------------------------
// Custom cell renderers for DataTable
// ---------------------------------------------------------------------------
const StageBadgeCell = ({ children, ...props }) => {
  const stageColorMap = {
    Prospecting: { bg: '#eef4ff', color: '#0176d3', border: '#b9d6f7' },
    Qualification: { bg: '#f0f0ff', color: '#9050e9', border: '#d4b8f0' },
    'Needs Analysis': { bg: '#fef3e8', color: '#fe9339', border: '#fdd5a0' },
    Proposal: { bg: '#e6f7f3', color: '#06a59a', border: '#a8e5de' },
    Negotiation: { bg: '#fef1f1', color: '#c23934', border: '#f4b4b1' },
    'Closed Won': { bg: '#e8f5e9', color: '#2e844a', border: '#a8d8b0' },
    'Closed Lost': { bg: '#f5f5f5', color: '#706e6b', border: '#c9c7c5' },
  };

  const stage = props.item && props.item.stage;
  const colorSet = stageColorMap[stage] || { bg: '#f3f3f3', color: '#706e6b', border: '#dddbda' };

  return (
    <DataTableCell {...props}>
      <span
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: colorSet.bg,
          color: colorSet.color,
          border: `1px solid ${colorSet.border}`,
          whiteSpace: 'nowrap',
        }}
      >
        {stage}
      </span>
    </DataTableCell>
  );
};
StageBadgeCell.displayName = DataTableCell.displayName;

const SparklineCell = ({ children, ...props }) => {
  const trendData = props.item && props.item.trendData;
  const trendDirection = props.item && props.item.trendDirection;
  const sparkColor = trendDirection === 'up' ? COLORS.success : COLORS.error;

  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {trendData && <SparkLine data={trendData} color={sparkColor} width={72} height={24} />}
        <Icon
          category="utility"
          name={trendDirection === 'up' ? 'arrowup' : 'arrowdown'}
          size="xx-small"
          style={{ fill: sparkColor }}
        />
      </div>
    </DataTableCell>
  );
};
SparklineCell.displayName = DataTableCell.displayName;

const CurrencyCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
CurrencyCell.displayName = DataTableCell.displayName;

const OwnerCell = ({ children, ...props }) => {
  const owner = props.item && props.item.owner;
  const initials = owner
    ? owner
        .split(' ')
        .map((n) => n[0])
        .join('')
    : '';
  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            backgroundColor: COLORS.brand,
            color: COLORS.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: '700',
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <span style={{ fontSize: '13px' }}>{owner}</span>
      </div>
    </DataTableCell>
  );
};
OwnerCell.displayName = DataTableCell.displayName;

const CloseDateCell = ({ children, ...props }) => {
  const closeDate = props.item && props.item.closeDate;
  if (!closeDate) {
    return <DataTableCell {...props}><span>--</span></DataTableCell>;
  }
  const date = new Date(closeDate);
  const today = new Date();
  const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  const isOverdue = daysUntil < 0;
  const isNearDue = daysUntil >= 0 && daysUntil <= 7;

  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '13px' }}>
          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: isOverdue ? COLORS.error : isNearDue ? COLORS.warning : COLORS.textSecondary,
            fontWeight: isOverdue || isNearDue ? '600' : '400',
          }}
        >
          {isOverdue
            ? `${Math.abs(daysUntil)} days overdue`
            : daysUntil === 0
            ? 'Due today'
            : `${daysUntil} days left`}
        </span>
      </div>
    </DataTableCell>
  );
};
CloseDateCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const ExecutiveDashboard = () => {
  // State
  const [timeframe, setTimeframe] = useState('QTD');
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [departmentSelection, setDepartmentSelection] = useState([DEPARTMENT_OPTIONS[0]]);
  const [departmentInputValue, setDepartmentInputValue] = useState('');

  // ---------------------------------------------------------------------------
  // Sample data -- all memoized
  // ---------------------------------------------------------------------------
  const factor = TIMEFRAME_FACTORS[timeframe];

  const revenueRawData = useMemo(() => generateRevenueData(), []);
  const pipelineRawData = useMemo(() => generatePipelineByStage(), []);
  const teamRawData = useMemo(() => generateTeamPerformance(), []);

  // KPI metrics
  const kpiMetrics = useMemo(
    () => [
      {
        id: 'revenue',
        title: 'Total Revenue',
        baseValue: 2847500,
        format: 'currency',
        change: 12.4,
        icon: 'moneybag',
      },
      {
        id: 'pipeline',
        title: 'Pipeline Value',
        baseValue: 8425000,
        format: 'currency',
        change: 8.7,
        icon: 'trending',
      },
      {
        id: 'winRate',
        title: 'Win Rate',
        baseValue: 34.2,
        format: 'percent',
        change: 3.1,
        icon: 'check',
      },
      {
        id: 'dealSize',
        title: 'Avg Deal Size',
        baseValue: 47800,
        format: 'currency',
        change: -2.3,
        icon: 'contract',
      },
      {
        id: 'accounts',
        title: 'Active Accounts',
        baseValue: 312,
        format: 'number',
        change: 5.6,
        icon: 'company',
      },
    ],
    []
  );

  // Compute adjusted KPI values based on timeframe
  const adjustedKPIs = useMemo(() => {
    return kpiMetrics.map((kpi) => {
      let adjustedValue = kpi.baseValue;
      if (kpi.format === 'currency') {
        adjustedValue = Math.round(kpi.baseValue * factor);
      } else if (kpi.format === 'percent') {
        // Win rate varies slightly per timeframe
        adjustedValue = parseFloat(
          (kpi.baseValue + (factor - 1) * 1.2).toFixed(1)
        );
      } else {
        adjustedValue = Math.round(kpi.baseValue * (1 + (factor - 1) * 0.08));
      }
      return { ...kpi, value: adjustedValue };
    });
  }, [kpiMetrics, factor]);

  // Revenue chart data
  const revenueChartData = useMemo(() => {
    const months = revenueRawData.labels;
    const sliceCount = timeframe === 'MTD' ? 1 : timeframe === 'QTD' ? 3 : 12;
    const labels = months.slice(0, sliceCount);
    const actualSlice = revenueRawData.actual.slice(0, sliceCount);
    const targetSlice = revenueRawData.target.slice(0, sliceCount);

    return {
      labels,
      datasets: [
        {
          label: 'Actual Revenue',
          data: actualSlice,
          borderColor: COLORS.brand,
          backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointBackgroundColor: COLORS.brand,
          pointBorderColor: COLORS.white,
          pointBorderWidth: 2,
          borderWidth: 2.5,
        },
        {
          label: 'Target Revenue',
          data: targetSlice,
          borderColor: COLORS.textMuted,
          backgroundColor: 'transparent',
          borderDash: [6, 4],
          tension: 0.35,
          pointRadius: 3,
          pointBackgroundColor: COLORS.textMuted,
          pointBorderColor: COLORS.white,
          pointBorderWidth: 2,
          borderWidth: 2,
          fill: false,
        },
      ],
    };
  }, [revenueRawData, timeframe]);

  const revenueChartOptions = useMemo(
    () => ({
      plugins: {
        legend: { position: 'top', align: 'end' },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`,
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (v) => `$${(v / 1000).toFixed(0)}K`,
          },
        },
      },
    }),
    []
  );

  // Pipeline doughnut data
  const pipelineChartData = useMemo(() => {
    const adjustedValues = pipelineRawData.values.map((v) =>
      Math.round(v * (factor * 0.35))
    );
    return {
      labels: pipelineRawData.labels,
      datasets: [
        {
          data: adjustedValues,
          backgroundColor: [
            COLORS.brand,
            COLORS.success,
            COLORS.warning,
            COLORS.error,
            COLORS.purple,
          ],
          borderColor: COLORS.white,
          borderWidth: 3,
          hoverOffset: 8,
        },
      ],
    };
  }, [pipelineRawData, factor]);

  const pipelineChartOptions = useMemo(
    () => ({
      cutout: '62%',
      plugins: {
        legend: { position: 'right', align: 'center' },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
              const pct = ((ctx.parsed / total) * 100).toFixed(1);
              return `${ctx.label}: $${(ctx.parsed / 1000).toFixed(0)}K (${pct}%)`;
            },
          },
        },
      },
    }),
    []
  );

  // Pipeline total for center label display
  const pipelineTotal = useMemo(() => {
    if (!pipelineChartData.datasets[0]) return 0;
    return pipelineChartData.datasets[0].data.reduce((a, b) => a + b, 0);
  }, [pipelineChartData]);

  // Top accounts horizontal bar data
  const topAccountsData = useMemo(
    () => ({
      labels: [
        'Acme Corp',
        'Global Tech',
        'Pinnacle Solutions',
        'Summit Industries',
        'Vertex Systems',
        'Horizon Group',
        'Atlas Partners',
        'Nova Dynamics',
      ],
      datasets: [
        {
          label: 'Revenue',
          data: [
            Math.round(485000 * factor),
            Math.round(412000 * factor),
            Math.round(378000 * factor),
            Math.round(345000 * factor),
            Math.round(298000 * factor),
            Math.round(267000 * factor),
            Math.round(234000 * factor),
            Math.round(198000 * factor),
          ],
          backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
          borderColor: COLORS.brand,
          borderWidth: 1.5,
          borderRadius: 3,
          barPercentage: 0.7,
        },
      ],
    }),
    [factor]
  );

  const topAccountsOptions = useMemo(
    () => ({
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `Revenue: $${ctx.parsed.x.toLocaleString()}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            callback: (v) => `$${(v / 1000).toFixed(0)}K`,
          },
        },
      },
    }),
    []
  );

  // Win rate trend line chart data
  const winRateChartData = useMemo(() => {
    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sliceCount = timeframe === 'MTD' ? 1 : timeframe === 'QTD' ? 3 : 12;
    const base = [28.5, 30.1, 29.8, 32.4, 31.7, 33.2, 34.0, 33.6, 35.1, 34.8, 36.2, 37.0];
    return {
      labels: labels.slice(0, sliceCount),
      datasets: [
        {
          label: 'Win Rate %',
          data: base.slice(0, sliceCount),
          borderColor: COLORS.success,
          backgroundColor: SLDS_CHART_COLORS_ALPHA[1],
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: COLORS.success,
          pointBorderColor: COLORS.white,
          pointBorderWidth: 2,
          borderWidth: 2.5,
        },
      ],
    };
  }, [timeframe]);

  const winRateChartOptions = useMemo(
    () => ({
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `Win Rate: ${ctx.parsed.y}%`,
          },
        },
      },
      scales: {
        y: {
          min: 20,
          max: 45,
          ticks: { callback: (v) => `${v}%` },
        },
      },
    }),
    []
  );

  // Team performance radar chart data
  const teamRadarData = useMemo(() => {
    const teamData = teamRawData;
    const topMembers = teamData.datasets.slice(0, 3);
    return {
      labels: teamData.labels,
      datasets: topMembers.map((member, i) => ({
        label: member.name,
        data: member.values,
        borderColor: SLDS_CHART_COLORS[i],
        backgroundColor: SLDS_CHART_COLORS_ALPHA[i],
        borderWidth: 2,
        pointRadius: 3,
        pointBackgroundColor: SLDS_CHART_COLORS[i],
        pointBorderColor: COLORS.white,
        pointBorderWidth: 1,
      })),
    };
  }, [teamRawData]);

  const teamRadarOptions = useMemo(
    () => ({
      plugins: {
        legend: { position: 'bottom' },
      },
      scales: {
        r: {
          min: 0,
          max: 100,
          ticks: { stepSize: 20 },
        },
      },
    }),
    []
  );

  // Key deals table data
  const keyDeals = useMemo(
    () => [
      {
        id: 'deal-1',
        name: 'Acme Corp - Enterprise Suite',
        account: 'Acme Corp',
        amount: Math.round(485000 * (factor * 0.3 + 0.7)),
        stage: 'Negotiation',
        owner: 'Sarah Johnson',
        closeDate: '2026-03-15',
        trendData: [42, 45, 44, 48, 51, 53, 56],
        trendDirection: 'up',
      },
      {
        id: 'deal-2',
        name: 'Global Tech - Analytics Add-on',
        account: 'Global Tech',
        amount: Math.round(312000 * (factor * 0.3 + 0.7)),
        stage: 'Proposal',
        owner: 'James Chen',
        closeDate: '2026-02-28',
        trendData: [38, 40, 37, 42, 44, 43, 47],
        trendDirection: 'up',
      },
      {
        id: 'deal-3',
        name: 'Pinnacle Solutions - Pro License',
        account: 'Pinnacle Solutions',
        amount: Math.round(275000 * (factor * 0.3 + 0.7)),
        stage: 'Closed Won',
        owner: 'Maria Garcia',
        closeDate: '2026-01-20',
        trendData: [30, 35, 40, 48, 55, 62, 70],
        trendDirection: 'up',
      },
      {
        id: 'deal-4',
        name: 'Summit Industries - Custom Integration',
        account: 'Summit Industries',
        amount: Math.round(198000 * (factor * 0.3 + 0.7)),
        stage: 'Needs Analysis',
        owner: 'Robert Williams',
        closeDate: '2026-04-10',
        trendData: [25, 28, 26, 30, 29, 31, 33],
        trendDirection: 'up',
      },
      {
        id: 'deal-5',
        name: 'Vertex Systems - Data Migration',
        account: 'Vertex Systems',
        amount: Math.round(167000 * (factor * 0.3 + 0.7)),
        stage: 'Qualification',
        owner: 'Emily Patel',
        closeDate: '2026-05-01',
        trendData: [20, 22, 19, 21, 18, 17, 15],
        trendDirection: 'down',
      },
      {
        id: 'deal-6',
        name: 'Horizon Group - Support Package',
        account: 'Horizon Group',
        amount: Math.round(145000 * (factor * 0.3 + 0.7)),
        stage: 'Prospecting',
        owner: 'Michael Kim',
        closeDate: '2026-06-15',
        trendData: [10, 12, 15, 14, 18, 20, 22],
        trendDirection: 'up',
      },
      {
        id: 'deal-7',
        name: 'Atlas Partners - Training Bundle',
        account: 'Atlas Partners',
        amount: Math.round(132000 * (factor * 0.3 + 0.7)),
        stage: 'Negotiation',
        owner: 'Jessica Anderson',
        closeDate: '2026-03-01',
        trendData: [35, 33, 30, 28, 25, 22, 20],
        trendDirection: 'down',
      },
      {
        id: 'deal-8',
        name: 'Nova Dynamics - API Access',
        account: 'Nova Dynamics',
        amount: Math.round(118000 * (factor * 0.3 + 0.7)),
        stage: 'Proposal',
        owner: 'David Martinez',
        closeDate: '2026-04-20',
        trendData: [28, 30, 32, 35, 38, 40, 43],
        trendDirection: 'up',
      },
      {
        id: 'deal-9',
        name: 'Apex Digital - Enterprise Suite',
        account: 'Apex Digital',
        amount: Math.round(97000 * (factor * 0.3 + 0.7)),
        stage: 'Closed Lost',
        owner: 'Ashley Thompson',
        closeDate: '2026-01-05',
        trendData: [45, 40, 35, 28, 20, 12, 8],
        trendDirection: 'down',
      },
      {
        id: 'deal-10',
        name: 'Orbit Technologies - Pro License',
        account: 'Orbit Technologies',
        amount: Math.round(86000 * (factor * 0.3 + 0.7)),
        stage: 'Qualification',
        owner: 'Daniel Lee',
        closeDate: '2026-05-15',
        trendData: [15, 18, 20, 22, 25, 28, 30],
        trendDirection: 'up',
      },
      {
        id: 'deal-11',
        name: 'Catalyst Inc - Analytics Add-on',
        account: 'Catalyst Inc',
        amount: Math.round(74000 * (factor * 0.3 + 0.7)),
        stage: 'Proposal',
        owner: 'Lauren Wilson',
        closeDate: '2026-03-30',
        trendData: [22, 25, 24, 28, 30, 33, 35],
        trendDirection: 'up',
      },
      {
        id: 'deal-12',
        name: 'Zenith Corp - Custom Integration',
        account: 'Zenith Corp',
        amount: Math.round(62000 * (factor * 0.3 + 0.7)),
        stage: 'Needs Analysis',
        owner: 'Chris Taylor',
        closeDate: '2026-06-01',
        trendData: [18, 16, 14, 12, 10, 8, 6],
        trendDirection: 'down',
      },
    ],
    [factor]
  );

  // AI insights data
  const aiInsights = useMemo(
    () => [
      {
        id: 'insight-1',
        type: 'warning',
        title: 'Pipeline Risk Detected',
        description:
          'Three deals worth a combined $497K in the Negotiation stage have not had activity in 14+ days. Consider scheduling follow-up calls with Acme Corp, Atlas Partners, and Beacon Analytics to maintain momentum.',
        metric: '$497K at risk',
        action: 'Review Stalled Deals',
      },
      {
        id: 'insight-2',
        type: 'success',
        title: 'Win Rate Trending Up',
        description:
          'Your team win rate has increased 3.1% over the prior period, driven by stronger qualification processes in the Sales team. The Proposal-to-Close conversion rate improved by 8.2% this quarter.',
        metric: '+3.1% improvement',
        action: 'View Win Analysis',
      },
      {
        id: 'insight-3',
        type: 'info',
        title: 'Revenue Forecast Update',
        description:
          'Based on current pipeline velocity and historical conversion rates, projected Q1 close revenue is $3.2M -- 107% of target. The top contributing segments are Enterprise Suite ($1.4M) and Analytics Add-on ($890K).',
        metric: '107% of target',
        action: 'View Forecast',
      },
      {
        id: 'insight-4',
        type: 'error',
        title: 'Account Health Alert',
        description:
          'Customer satisfaction scores for Vertex Systems dropped 18 points this month. Their renewal is due in 45 days. Recommend immediate executive engagement and a dedicated success plan review.',
        metric: 'CSAT -18 pts',
        action: 'View Account',
      },
    ],
    []
  );

  // Activity summary data for the side panel
  const activitySummary = useMemo(
    () => ({
      calls: { count: Math.round(142 * factor), change: 8 },
      emails: { count: Math.round(387 * factor), change: -3 },
      meetings: { count: Math.round(64 * factor), change: 15 },
      tasks: { count: Math.round(218 * factor), change: 5 },
    }),
    [factor]
  );

  // Quarterly targets data
  const quarterlyTargets = useMemo(
    () => [
      { label: 'Revenue', current: 2847500, target: 3200000, color: COLORS.brand },
      { label: 'New Logos', current: 18, target: 25, color: COLORS.success },
      { label: 'Expansion', current: 720000, target: 900000, color: COLORS.purple },
      { label: 'Retention', current: 94.2, target: 95.0, color: COLORS.teal },
    ],
    []
  );

  // Regional breakdown data
  const regionalData = useMemo(
    () => [
      { region: 'North America', revenue: Math.round(1520000 * factor), deals: 47, growth: 14.2 },
      { region: 'Europe', revenue: Math.round(680000 * factor), deals: 23, growth: 8.7 },
      { region: 'Asia Pacific', revenue: Math.round(420000 * factor), deals: 15, growth: 22.1 },
      { region: 'Latin America', revenue: Math.round(227500 * factor), deals: 9, growth: -3.4 },
    ],
    [factor]
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
  };

  const handleKPIClick = (kpiId) => {
    setSelectedKPI(selectedKPI === kpiId ? null : kpiId);
  };

  // ---------------------------------------------------------------------------
  // Insight type styles
  // ---------------------------------------------------------------------------
  const insightTypeConfig = {
    warning: {
      borderColor: COLORS.warning,
      iconName: 'warning',
      iconColor: COLORS.warning,
      bgColor: '#fef8f0',
    },
    success: {
      borderColor: COLORS.success,
      iconName: 'success',
      iconColor: COLORS.success,
      bgColor: '#eaf5ea',
    },
    info: {
      borderColor: COLORS.brand,
      iconName: 'info',
      iconColor: COLORS.brand,
      bgColor: '#eef4ff',
    },
    error: {
      borderColor: COLORS.error,
      iconName: 'error',
      iconColor: COLORS.error,
      bgColor: '#fef1f1',
    },
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <ShowcasePageWrapper
      title="Executive Dashboard"
      description="Full analytics dashboard with KPIs, charts, and insights"
      icon="dashboard"
      iconCategory="standard"
    >
      {/* ================================================================= */}
      {/* HEADER: Timeframe toggle + Department filter + Last Updated       */}
      {/* ================================================================= */}
      <div
        style={{
          ...cardStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon category="utility" name="date_input" size="x-small" style={{ fill: COLORS.textSecondary }} />
            <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textSecondary }}>
              Timeframe:
            </span>
            <ButtonGroup variant="list" id="timeframe-toggle">
              {['MTD', 'QTD', 'YTD'].map((tf) => (
                <Button
                  key={tf}
                  label={tf}
                  variant={timeframe === tf ? 'brand' : 'neutral'}
                  onClick={() => handleTimeframeChange(tf)}
                />
              ))}
            </ButtonGroup>
          </div>

          <div
            style={{
              width: '1px',
              height: '28px',
              backgroundColor: COLORS.border,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '240px' }}>
            <Icon category="utility" name="filterList" size="x-small" style={{ fill: COLORS.textSecondary }} />
            <Combobox
              id="department-filter"
              labels={{ placeholder: 'Filter by Department' }}
              options={DEPARTMENT_OPTIONS.map((opt) => ({
                ...opt,
                value: opt.id,
              }))}
              selection={departmentSelection}
              value={departmentInputValue}
              variant="readonly"
              onSelect={(event, data) => {
                if (data.selection.length > 0) {
                  setDepartmentSelection(data.selection);
                }
              }}
              onChange={(event, { value: val }) => {
                setDepartmentInputValue(val);
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
            Last updated: {new Date().toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </span>
          <Button
            iconCategory="utility"
            iconName="refresh"
            variant="icon"
            iconSize="small"
            assistiveText={{ icon: 'Refresh dashboard' }}
          />
          <Button
            iconCategory="utility"
            iconName="download"
            variant="icon"
            iconSize="small"
            assistiveText={{ icon: 'Export dashboard' }}
          />
        </div>
      </div>

      {/* ================================================================= */}
      {/* Timeframe description bar                                         */}
      {/* ================================================================= */}
      <div
        style={{
          backgroundColor: '#eef4ff',
          border: `1px solid ${COLORS.brand}33`,
          borderRadius: '4px',
          padding: '10px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Icon category="utility" name="info_alt" size="x-small" style={{ fill: COLORS.brand }} />
        <span style={{ fontSize: '13px', color: COLORS.brand }}>
          Showing <strong>{TIMEFRAME_LABELS[timeframe]}</strong> data for{' '}
          <strong>
            {departmentSelection[0] && departmentSelection[0].label
              ? departmentSelection[0].label
              : 'All Departments'}
          </strong>
          . All values are compared against the prior equivalent period.
        </span>
      </div>

      {/* ================================================================= */}
      {/* KPI ROW: 5 Metric cards                                           */}
      {/* ================================================================= */}
      <div
        style={{
          ...gridRow('repeat(5, 1fr)', '14px'),
          marginBottom: '16px',
        }}
      >
        {adjustedKPIs.map((kpi) => (
          <MetricCard
            key={kpi.id}
            title={kpi.title}
            value={kpi.value}
            format={kpi.format}
            change={kpi.change}
            icon={kpi.icon}
            onClick={() => handleKPIClick(kpi.id)}
            selected={selectedKPI === kpi.id}
          />
        ))}
      </div>

      {/* ================================================================= */}
      {/* CHARTS ROW 1: Revenue Trend | Pipeline by Stage                   */}
      {/* ================================================================= */}
      <div style={{ ...gridRow('3fr 2fr'), marginBottom: '16px' }}>
        {/* Revenue Trend */}
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px',
            }}
          >
            <div>
              <h3 style={sectionHeaderStyle}>Revenue Trend</h3>
              <p style={sectionSubtitleStyle}>Actual vs target revenue by month</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '3px',
                    backgroundColor: COLORS.brand,
                    borderRadius: '2px',
                  }}
                />
                <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>Actual</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '3px',
                    backgroundColor: COLORS.textMuted,
                    borderRadius: '2px',
                    borderTop: '1px dashed transparent',
                  }}
                />
                <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>Target</span>
              </div>
            </div>
          </div>
          <SLDSLineChart data={revenueChartData} options={revenueChartOptions} height={320} />
        </div>

        {/* Pipeline by Stage */}
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={sectionHeaderStyle}>Pipeline by Stage</h3>
            <p style={sectionSubtitleStyle}>Current distribution across sales stages</p>
          </div>
          <div style={{ position: 'relative' }}>
            <SLDSDoughnutChart data={pipelineChartData} options={pipelineChartOptions} height={280} />
            {/* Center label overlay */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '38%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '2px' }}>
                Total Pipeline
              </div>
              <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary }}>
                ${(pipelineTotal / 1000000).toFixed(1)}M
              </div>
            </div>
          </div>
          {/* Pipeline stage breakdown */}
          <div
            style={{
              marginTop: '16px',
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '8px',
              borderTop: `1px solid ${COLORS.border}`,
              paddingTop: '12px',
            }}
          >
            {pipelineRawData.labels.map((label, i) => {
              const adjustedVal = pipelineChartData.datasets[0].data[i];
              const total = pipelineChartData.datasets[0].data.reduce((a, b) => a + b, 0);
              const pct = ((adjustedVal / total) * 100).toFixed(0);
              const stageColors = [COLORS.brand, COLORS.success, COLORS.warning, COLORS.error, COLORS.purple];
              return (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: stageColors[i],
                      display: 'inline-block',
                      marginBottom: '4px',
                    }}
                  />
                  <div style={{ fontSize: '10px', color: COLORS.textSecondary, marginBottom: '2px' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.textPrimary }}>
                    {pct}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* CHARTS ROW 2: Top Accounts | Win Rate | Team Performance          */}
      {/* ================================================================= */}
      <div style={{ ...gridRow('1fr 1fr 1fr'), marginBottom: '16px' }}>
        {/* Top Accounts */}
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px',
            }}
          >
            <div>
              <h3 style={sectionHeaderStyle}>Top Accounts</h3>
              <p style={sectionSubtitleStyle}>Revenue by account, ranked</p>
            </div>
            <Button
              label="View All"
              variant="base"
              style={{ fontSize: '12px', color: COLORS.brand }}
            />
          </div>
          <SLDSBarChart data={topAccountsData} options={topAccountsOptions} height={300} />
        </div>

        {/* Win Rate Trend */}
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px',
            }}
          >
            <div>
              <h3 style={sectionHeaderStyle}>Win Rate Trend</h3>
              <p style={sectionSubtitleStyle}>Monthly close rate percentage</p>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: '#eaf5ea',
                padding: '4px 10px',
                borderRadius: '12px',
              }}
            >
              <Icon category="utility" name="up" size="xx-small" style={{ fill: COLORS.success }} />
              <span style={{ fontSize: '12px', fontWeight: '700', color: COLORS.success }}>
                +3.1%
              </span>
            </div>
          </div>
          <SLDSLineChart data={winRateChartData} options={winRateChartOptions} height={300} />
        </div>

        {/* Team Performance */}
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px',
            }}
          >
            <div>
              <h3 style={sectionHeaderStyle}>Team Performance</h3>
              <p style={sectionSubtitleStyle}>Multi-axis comparison of top reps</p>
            </div>
            <Button
              label="Details"
              variant="base"
              style={{ fontSize: '12px', color: COLORS.brand }}
            />
          </div>
          <SLDSRadarChart data={teamRadarData} options={teamRadarOptions} height={300} />
        </div>
      </div>

      {/* ================================================================= */}
      {/* ACTIVITY + TARGETS ROW                                            */}
      {/* ================================================================= */}
      <div style={{ ...gridRow('1fr 1fr'), marginBottom: '16px' }}>
        {/* Activity Summary */}
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={sectionHeaderStyle}>Activity Summary</h3>
            <p style={sectionSubtitleStyle}>Team engagement metrics for the selected period</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {[
              {
                label: 'Calls Made',
                icon: 'call',
                count: activitySummary.calls.count,
                change: activitySummary.calls.change,
                color: COLORS.brand,
              },
              {
                label: 'Emails Sent',
                icon: 'email',
                count: activitySummary.emails.count,
                change: activitySummary.emails.change,
                color: COLORS.purple,
              },
              {
                label: 'Meetings Held',
                icon: 'event',
                count: activitySummary.meetings.count,
                change: activitySummary.meetings.change,
                color: COLORS.success,
              },
              {
                label: 'Tasks Completed',
                icon: 'task',
                count: activitySummary.tasks.count,
                change: activitySummary.tasks.change,
                color: COLORS.teal,
              },
            ].map((activity) => (
              <div
                key={activity.label}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '4px',
                  padding: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: COLORS.bg,
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor: activity.color + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    category="utility"
                    name={activity.icon}
                    size="small"
                    style={{ fill: activity.color }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '2px' }}>
                    {activity.label}
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary }}>
                    {activity.count.toLocaleString()}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  <Icon
                    category="utility"
                    name={activity.change >= 0 ? 'up' : 'down'}
                    size="xx-small"
                    style={{ fill: activity.change >= 0 ? COLORS.success : COLORS.error }}
                  />
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: activity.change >= 0 ? COLORS.success : COLORS.error,
                    }}
                  >
                    {Math.abs(activity.change)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quarterly Targets */}
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={sectionHeaderStyle}>Quarterly Targets</h3>
            <p style={sectionSubtitleStyle}>Progress toward Q1 objectives</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {quarterlyTargets.map((target) => {
              const isCurrency = target.label === 'Revenue' || target.label === 'Expansion';
              const isPercent = target.label === 'Retention';
              const progress = Math.min((target.current / target.target) * 100, 100);
              const formatVal = (v) => {
                if (isCurrency) return `$${(v / 1000000).toFixed(2)}M`;
                if (isPercent) return `${v}%`;
                return v.toString();
              };

              return (
                <div key={target.label}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                      {target.label}
                    </span>
                    <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      {formatVal(target.current)} / {formatVal(target.target)}
                    </span>
                  </div>
                  <div
                    style={{
                      height: '8px',
                      backgroundColor: '#e5e5e5',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: target.color,
                        borderRadius: '4px',
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '4px',
                    }}
                  >
                    <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                      {progress.toFixed(0)}% complete
                    </span>
                    <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                      {progress >= 100
                        ? 'Target met'
                        : `${(100 - progress).toFixed(0)}% remaining`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* REGIONAL BREAKDOWN ROW                                            */}
      {/* ================================================================= */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
          }}
        >
          <div>
            <h3 style={sectionHeaderStyle}>Regional Performance</h3>
            <p style={sectionSubtitleStyle}>Revenue breakdown and growth by geography</p>
          </div>
          <Button
            label="Full Report"
            variant="base"
            style={{ fontSize: '12px', color: COLORS.brand }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
          {regionalData.map((region) => {
            const totalRevenue = regionalData.reduce((sum, r) => sum + r.revenue, 0);
            const sharePercent = ((region.revenue / totalRevenue) * 100).toFixed(1);
            return (
              <div
                key={region.region}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '4px',
                  padding: '16px',
                  backgroundColor: COLORS.bg,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: COLORS.textPrimary,
                    }}
                  >
                    {region.region}
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                    }}
                  >
                    <Icon
                      category="utility"
                      name={region.growth >= 0 ? 'up' : 'down'}
                      size="xx-small"
                      style={{ fill: region.growth >= 0 ? COLORS.success : COLORS.error }}
                    />
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: region.growth >= 0 ? COLORS.success : COLORS.error,
                      }}
                    >
                      {Math.abs(region.growth)}%
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '24px',
                    fontWeight: '300',
                    color: COLORS.textPrimary,
                    marginBottom: '6px',
                  }}
                >
                  ${(region.revenue / 1000000).toFixed(2)}M
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                    {region.deals} deals
                  </span>
                  <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                    {sharePercent}% of total
                  </span>
                </div>
                {/* Mini progress bar showing share */}
                <div
                  style={{
                    height: '4px',
                    backgroundColor: '#e5e5e5',
                    borderRadius: '2px',
                    marginTop: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${sharePercent}%`,
                      height: '100%',
                      backgroundColor: COLORS.brand,
                      borderRadius: '2px',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ================================================================= */}
      {/* KEY DEALS TABLE                                                   */}
      {/* ================================================================= */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <h3 style={sectionHeaderStyle}>Key Deals</h3>
            <p style={{ ...sectionSubtitleStyle, marginBottom: 0 }}>
              Top opportunities being tracked this period
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              label="Export"
              variant="neutral"
              iconCategory="utility"
              iconName="download"
              iconPosition="left"
            />
            <Button
              label="View All Deals"
              variant="brand"
              iconCategory="utility"
              iconName="open"
              iconPosition="right"
            />
          </div>
        </div>
        <DataTable items={keyDeals} id="key-deals-table" fixedLayout>
          <DataTableColumn label="Deal Name" property="name" width="220px" />
          <DataTableColumn label="Account" property="account" width="150px" />
          <DataTableColumn label="Amount" property="amount" width="130px">
            <CurrencyCell />
          </DataTableColumn>
          <DataTableColumn label="Stage" property="stage" width="140px">
            <StageBadgeCell />
          </DataTableColumn>
          <DataTableColumn label="Owner" property="owner" width="180px">
            <OwnerCell />
          </DataTableColumn>
          <DataTableColumn label="Close Date" property="closeDate" width="140px">
            <CloseDateCell />
          </DataTableColumn>
          <DataTableColumn label="Trend" property="trendData" width="110px">
            <SparklineCell />
          </DataTableColumn>
        </DataTable>

        {/* Table summary bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
            Showing {keyDeals.length} of {keyDeals.length} deals
          </span>
          <div style={{ display: 'flex', gap: '20px' }}>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              Total Value:{' '}
              <strong style={{ color: COLORS.textPrimary }}>
                ${keyDeals
                  .reduce((sum, d) => sum + d.amount, 0)
                  .toLocaleString('en-US')}
              </strong>
            </span>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              Avg Deal Size:{' '}
              <strong style={{ color: COLORS.textPrimary }}>
                ${Math.round(
                  keyDeals.reduce((sum, d) => sum + d.amount, 0) / keyDeals.length
                ).toLocaleString('en-US')}
              </strong>
            </span>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              Weighted Pipeline:{' '}
              <strong style={{ color: COLORS.textPrimary }}>
                ${Math.round(
                  keyDeals.reduce((sum, d) => sum + d.amount, 0) * 0.42
                ).toLocaleString('en-US')}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* AI INSIGHTS                                                       */}
      {/* ================================================================= */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#f0e6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon category="utility" name="magicwand" size="small" style={{ fill: COLORS.purple }} />
            </div>
            <div>
              <h3 style={{ ...sectionHeaderStyle, marginBottom: 0 }}>AI-Powered Insights</h3>
              <p style={{ ...sectionSubtitleStyle, marginBottom: 0, marginTop: '2px' }}>
                Intelligent recommendations based on your data patterns
              </p>
            </div>
          </div>
          <Button
            label="Configure Insights"
            variant="neutral"
            iconCategory="utility"
            iconName="settings"
            iconPosition="left"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
          {aiInsights.map((insight) => {
            const config = insightTypeConfig[insight.type];
            return (
              <div
                key={insight.id}
                style={{
                  border: `1px solid ${config.borderColor}33`,
                  borderLeft: `4px solid ${config.borderColor}`,
                  borderRadius: '4px',
                  padding: '16px',
                  backgroundColor: config.bgColor,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon
                      category="utility"
                      name={config.iconName}
                      size="x-small"
                      style={{ fill: config.iconColor }}
                    />
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: COLORS.textPrimary,
                      }}
                    >
                      {insight.title}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: config.iconColor,
                      backgroundColor: COLORS.white,
                      padding: '2px 8px',
                      borderRadius: '10px',
                      border: `1px solid ${config.borderColor}55`,
                    }}
                  >
                    {insight.metric}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: '13px',
                    color: COLORS.textSecondary,
                    lineHeight: '1.5',
                    margin: '0 0 12px 0',
                  }}
                >
                  {insight.description}
                </p>
                <Button
                  label={insight.action}
                  variant="base"
                  style={{ fontSize: '12px', color: config.iconColor, fontWeight: '600', padding: 0 }}
                  iconCategory="utility"
                  iconName="forward"
                  iconPosition="right"
                  iconSize="x-small"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* ================================================================= */}
      {/* SCOPED NOTIFICATION ALERTS                                        */}
      {/* ================================================================= */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
        <ScopedNotification theme="info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon category="utility" name="info" size="x-small" />
            <span>
              <strong>Quarterly business review</strong> is scheduled for March 15th. All department
              heads should prepare their summary reports by March 10th.
            </span>
          </div>
        </ScopedNotification>
        <ScopedNotification theme="warning">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon category="utility" name="warning" size="x-small" />
            <span>
              <strong>3 high-value deals</strong> are past their expected close date. Immediate
              attention recommended to prevent pipeline slippage.
            </span>
          </div>
        </ScopedNotification>
      </div>

      {/* ================================================================= */}
      {/* BOTTOM ROW: Recent Wins + Leaderboard                             */}
      {/* ================================================================= */}
      <div style={{ ...gridRow('1fr 1fr'), marginBottom: '16px' }}>
        {/* Recent Wins */}
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={sectionHeaderStyle}>Recent Wins</h3>
            <p style={sectionSubtitleStyle}>Latest closed-won deals</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              {
                deal: 'Pinnacle Solutions - Pro License',
                amount: 275000,
                owner: 'Maria Garcia',
                date: 'Jan 20, 2026',
                daysToClose: 34,
              },
              {
                deal: 'Forge Innovations - Enterprise Suite',
                amount: 198000,
                owner: 'Sarah Johnson',
                date: 'Jan 15, 2026',
                daysToClose: 28,
              },
              {
                deal: 'Pulse Networks - Analytics Add-on',
                amount: 156000,
                owner: 'James Chen',
                date: 'Jan 8, 2026',
                daysToClose: 42,
              },
              {
                deal: 'Beacon Analytics - Support Package',
                amount: 112000,
                owner: 'Emily Patel',
                date: 'Dec 28, 2025',
                daysToClose: 19,
              },
              {
                deal: 'Catalyst Inc - Training Bundle',
                amount: 89000,
                owner: 'Michael Kim',
                date: 'Dec 22, 2025',
                daysToClose: 51,
              },
            ].map((win, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: '4px',
                  backgroundColor: idx % 2 === 0 ? COLORS.bg : COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#eaf5ea',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon category="utility" name="check" size="xx-small" style={{ fill: COLORS.success }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                      {win.deal}
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                      {win.owner} &middot; {win.date} &middot; {win.daysToClose} days to close
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: COLORS.success,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${win.amount.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sales Leaderboard */}
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={sectionHeaderStyle}>Sales Leaderboard</h3>
            <p style={sectionSubtitleStyle}>Top performers this quarter</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { name: 'Sarah Johnson', revenue: 892000, deals: 12, quota: 118, rank: 1 },
              { name: 'James Chen', revenue: 756000, deals: 9, quota: 108, rank: 2 },
              { name: 'Maria Garcia', revenue: 645000, deals: 11, quota: 97, rank: 3 },
              { name: 'Robert Williams', revenue: 534000, deals: 7, quota: 89, rank: 4 },
              { name: 'Emily Patel', revenue: 478000, deals: 8, quota: 82, rank: 5 },
              { name: 'Michael Kim', revenue: 412000, deals: 6, quota: 74, rank: 6 },
              { name: 'Jessica Anderson', revenue: 367000, deals: 5, quota: 68, rank: 7 },
            ].map((rep) => {
              const initials = rep.name
                .split(' ')
                .map((n) => n[0])
                .join('');
              const isAboveQuota = rep.quota >= 100;
              const rankColors = {
                1: '#FFD700',
                2: '#C0C0C0',
                3: '#CD7F32',
              };
              return (
                <div
                  key={rep.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: rep.rank <= 3 ? '#fafaf8' : COLORS.white,
                  }}
                >
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: rankColors[rep.rank] || COLORS.bg,
                      color: rankColors[rep.rank] ? '#fff' : COLORS.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '700',
                      flexShrink: 0,
                      border: rankColors[rep.rank]
                        ? 'none'
                        : `1px solid ${COLORS.border}`,
                    }}
                  >
                    {rep.rank}
                  </div>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: SLDS_CHART_COLORS[rep.rank % SLDS_CHART_COLORS.length],
                      color: COLORS.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '700',
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                      {rep.name}
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                      {rep.deals} deals closed
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: COLORS.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      ${(rep.revenue / 1000).toFixed(0)}K
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: isAboveQuota ? COLORS.success : COLORS.warning,
                      }}
                    >
                      {rep.quota}% of quota
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* FORECAST SUMMARY BAR                                              */}
      {/* ================================================================= */}
      <div
        style={{
          ...cardStyle,
          background: `linear-gradient(135deg, ${COLORS.brand}08, ${COLORS.purple}08)`,
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
          }}
        >
          <div>
            <h3 style={sectionHeaderStyle}>Forecast Summary</h3>
            <p style={sectionSubtitleStyle}>Projected outcomes based on current pipeline velocity</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button label="Best Case" variant="neutral" />
            <Button label="Most Likely" variant="brand" />
            <Button label="Worst Case" variant="neutral" />
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '14px',
          }}
        >
          {[
            {
              label: 'Committed',
              value: Math.round(1840000 * factor),
              pct: 58,
              color: COLORS.success,
              desc: 'Deals in late stages with high confidence',
            },
            {
              label: 'Best Case',
              value: Math.round(3420000 * factor),
              pct: 107,
              color: COLORS.brand,
              desc: 'Includes all pipeline with 50%+ probability',
            },
            {
              label: 'Pipeline',
              value: Math.round(5680000 * factor),
              pct: 178,
              color: COLORS.warning,
              desc: 'Total open pipeline across all stages',
            },
            {
              label: 'Gap to Target',
              value: Math.round(360000 * factor),
              pct: 11,
              color: COLORS.error,
              desc: 'Additional revenue needed to hit quota',
            },
          ].map((forecast) => (
            <div
              key={forecast.label}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: '4px',
                padding: '16px',
                backgroundColor: COLORS.white,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                  {forecast.label}
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: forecast.color,
                    backgroundColor: forecast.color + '15',
                    padding: '2px 8px',
                    borderRadius: '10px',
                  }}
                >
                  {forecast.pct}%
                </span>
              </div>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: '300',
                  color: COLORS.textPrimary,
                  marginBottom: '6px',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                ${(forecast.value / 1000000).toFixed(2)}M
              </div>
              <p style={{ fontSize: '11px', color: COLORS.textSecondary, margin: 0, lineHeight: '1.4' }}>
                {forecast.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ================================================================= */}
      {/* FOOTER / DASHBOARD META                                           */}
      {/* ================================================================= */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
            Data refreshed every 15 minutes
          </span>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>|</span>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
            Fiscal Year: FY2026
          </span>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>|</span>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
            Currency: USD
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            label="Print Dashboard"
            variant="base"
            style={{ fontSize: '11px', color: COLORS.textMuted }}
            iconCategory="utility"
            iconName="print"
            iconPosition="left"
            iconSize="x-small"
          />
          <Button
            label="Share"
            variant="base"
            style={{ fontSize: '11px', color: COLORS.textMuted }}
            iconCategory="utility"
            iconName="share"
            iconPosition="left"
            iconSize="x-small"
          />
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default ExecutiveDashboard;
